import { useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, Shield } from 'lucide-react';
import { AnalyticsSection } from './admin/AnalyticsSection';
import { PricingSection } from './admin/PricingSection';
import { UsersSection } from './admin/UsersSection';
import { DevSyncSection } from './admin/DevSyncSection';
import { TranscriptsSection } from './admin/TranscriptsSection';
import { AppAnalyticsSection } from './admin/AppAnalyticsSection';
import { SectionNav } from '../SectionNav';
import { useNoticeAuth } from '../NoticeAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';

// Admin-only console: codebase analytics, the run economics (Costs & Quotas), the
// authorized-user directory, meeting transcriptions and (dev only) the prod→local sync. One section shows
// at a time, chosen from the numbered header bar. The guard lives here so a section
// fetches its admin endpoints on mount — only the one being viewed, now that they
// no longer all render at once. Dev Sync is stripped from the prod build.
export function NoticeAdmin() {
  const { user, isLoading: authLoading } = useNoticeAuth();
  const { t } = useLanguage();

  const sections: { id: string; label: string; el: ReactNode }[] = [
    { id: 'analytics', label: t('notice.analytics.title'), el: <AnalyticsSection /> },
    { id: 'costs', label: t('notice.admin.costsQuotas'), el: <PricingSection /> },
    {
      id: 'users',
      label: t('notice.users.title'),
      // ALL THREE ALLOWLISTS, one under the other, because the interesting thing about
      // them is the difference: who can open the real app, who can only be shown it, and
      // who can go and break a copy of it. Three separate databases behind these — see
      // UsersSection's `env`.
      el: (
        <div className="flex flex-col gap-10">
          <UsersSection env="app" />
          <UsersSection env="demo" />
          <UsersSection env="sandbox" />
        </div>
      ),
    },
    { id: 'transcripts', label: t('notice.transcripts.tab'), el: <TranscriptsSection /> },
    // What the app and the published tutorial ask of a device's memory, one pass at a time.
    { id: 'appAnalytics', label: t('notice.memory.tab'), el: <AppAnalyticsSection /> },
    ...(import.meta.env.DEV
      ? [{ id: 'devsync', label: 'Dev Sync', el: <DevSyncSection /> }]
      : []),
  ];
  const [active, setActive] = useState(sections[0].id);
  const current = sections.find((s) => s.id === active) ?? sections[0];

  if (authLoading) {
    return (
      <div className="flex justify-center py-16 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!user?.is_admin) return <Navigate to="/home" replace />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-center gap-3">
        <Shield className="h-6 w-6 text-blue-600" />
        <h1 className="font-sora text-2xl font-bold text-white">{t('notice.nav.admin')}</h1>
      </div>

      {/* NO RULES ON THE PAGE, as nowhere else in the app has them: each block is a white card
          with its title in it (see TableCard). */}
      <div className="my-8">
        <SectionNav
          items={sections.map(({ id, label }) => ({ id, label }))}
          active={active}
          onSelect={setActive}
        />
      </div>

      {current.el}
    </div>
  );
}
